import { useMemo } from 'react'

import CheckBoxIcon from '@mui/icons-material/CheckBox'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { Tooltip } from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid'

import { IGridColumn } from 'components/common'
import { BookingStatus, DriverAllocationStatus, DriverType } from 'generated/graphql'

const useGetAllocatedDriversColumns = (
  isAllowedToViewPrimaryDriversList,
  handleDriverNotes,
  deleteBookingDriverProcessing,
  handleEditAllocatedDriver,
  handleAddRating,
  handleDeleteDriver,
  bookingStatus,
  isCxManualAcceptEnabled: boolean,
  handleCxManualAcceptOpenDialog: () => void,
  courierExchangeLoadId?: string,
) => {
  const bookingStatusForRating =
    bookingStatus &&
    [BookingStatus.Invoice, BookingStatus.Complete, BookingStatus.ProofOfDelivery].includes(
      bookingStatus,
    )

  const DELETE_ALLOWED_STATUSES_BASE = [
    BookingStatus.Quotation,
    BookingStatus.EBooking,
    BookingStatus.Waiting,
    BookingStatus.Declined,
    BookingStatus.ScheduledTemplate,
  ]

  const DELETE_ALLOWED_STATUSES_ADMIN_ADDITIONAL = [
    BookingStatus.Allocated,
    BookingStatus.PackageOnBoardOnWay,
    BookingStatus.PackageOnBoardArrival,
  ]

  const canDeleteDriver = (() => {
    if (!bookingStatus) return false

    if (isAllowedToViewPrimaryDriversList) {
      return (
        DELETE_ALLOWED_STATUSES_BASE.includes(bookingStatus) ||
        DELETE_ALLOWED_STATUSES_ADMIN_ADDITIONAL.includes(bookingStatus)
      )
    }

    return DELETE_ALLOWED_STATUSES_BASE.includes(bookingStatus)
  })()

  // const REPLACE_ALLOWED_STATUSES_BASE = [
  //   BookingStatus.Quotation,
  //   BookingStatus.EBooking,
  //   BookingStatus.Waiting,
  //   BookingStatus.Declined,
  //   BookingStatus.ScheduledTemplate,
  // ]

  // const REPLACE_ALLOWED_STATUSES_ADMIN_ADDITIONAL = [
  //   BookingStatus.Allocated,
  //   BookingStatus.PackageOnBoardOnWay,
  //   BookingStatus.PackageOnBoardArrival,
  // ]

  // const canReplaceDriver = (() => {
  //   if (!bookingStatus) return false

  //   if (isAllowedToViewPrimaryDriversList) {
  //     return (
  //       REPLACE_ALLOWED_STATUSES_BASE.includes(bookingStatus) ||
  //       REPLACE_ALLOWED_STATUSES_ADMIN_ADDITIONAL.includes(bookingStatus)
  //     )
  //   }

  //   return REPLACE_ALLOWED_STATUSES_BASE.includes(bookingStatus)
  // })()

  const columns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'primaryDriverName',
        headerName: 'Company',
        minWidth: 150,
        flex: 1,
        sortable: false,
        valueGetter: ({ row }) => row?.driver.primaryDriverName,
      },
      {
        field: 'driver',
        headerName: 'Driver',
        minWidth: 150,
        flex: 1,
        sortPath: ['driver', 'name'],
        valueGetter: ({ value }) => value?.name,
      },
      { field: 'allocationStatus', headerName: 'Alloc. Status', width: 100 },
      {
        field: 'isUsingInternalApp',
        headerName: 'Using App?',
        width: 100,
        type: 'boolean',
        sortable: false,
        valueGetter: ({ row }) => row.driver?.isUsingInternalApp,
      },
      {
        field: 'reference',
        headerName: 'Reference',
        width: 100,
        type: 'string',
      },
      {
        field: 'vehicle',
        headerName: 'Vehicle',
        width: 120,
        sortPath: ['vehicle', 'vehicleType', 'name'],
        valueGetter: ({ value }) => value?.vehicleType?.name,
      },
      { field: 'charge', headerName: 'Charge', width: 80, type: 'number' },
      { field: 'extraPayment', headerName: 'Extra Pay', width: 80, type: 'number' },
      {
        field: 'currency',
        headerName: 'Ccy',
        width: 50,
        sortPath: ['currency', 'code'],
        valueGetter: ({ value }) => value?.code,
      },
      ...(isAllowedToViewPrimaryDriversList
        ? [
            {
              field: 'xeroPurchaseCode',
              headerName: 'Purchase Code',
              width: 120,
              type: 'number',
            },
          ]
        : []),
      { field: 'createdAt', headerName: 'Created At', hide: true },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        type: 'actions',
        getActions: ({ id, row }) => [
          <GridActionsCellItem
            key={`note-${id}`}
            label='Driver note'
            size='medium'
            disabled={!row.driverNotes}
            icon={
              <Tooltip title={'Driver note'} arrow>
                <StickyNote2OutlinedIcon />
              </Tooltip>
            }
            onClick={handleDriverNotes(row.driverNotes)}
          />,
          <GridActionsCellItem
            key={`edit-${id}`}
            label='Edit'
            size='medium'
            // disabled={deleteBookingDriverProcessing || !canReplaceDriver}
            icon={
              <Tooltip title={'Edit'} arrow>
                <EditIcon />
              </Tooltip>
            }
            onClick={handleEditAllocatedDriver(row)}
          />,
          <GridActionsCellItem
            sx={{ display: bookingStatusForRating ? 'block' : 'none' }}
            key={`rating-${id}`}
            label='Rating'
            size='medium'
            icon={
              <Tooltip title={'Rating'} arrow>
                {row.rating ? <StarIcon sx={{ color: '#faaf00' }} /> : <StarBorderIcon />}
              </Tooltip>
            }
            onClick={handleAddRating(row.driver.id)}
          />,
          <GridActionsCellItem
            sx={{
              display: isCxManualAcceptEnabled ? 'flex' : 'none',
            }}
            key={`cx-manual-accept-${id}`}
            label='CX Manual Accept'
            size='medium'
            icon={
              <Tooltip title={'CX Manual Accept Booking'} arrow>
                <CheckBoxIcon />
              </Tooltip>
            }
            disabled={
              row.allocationStatus === DriverAllocationStatus.Accepted ||
              row.allocationStatus === DriverAllocationStatus.Declined ||
              row.driver?.driverType !== DriverType.Cx ||
              !courierExchangeLoadId
            }
            onClick={handleCxManualAcceptOpenDialog}
          />,
          <GridActionsCellItem
            key={`delete-${id}`}
            label='Delete'
            size='medium'
            disabled={deleteBookingDriverProcessing || !canDeleteDriver}
            icon={
              <Tooltip title={'Delete'} arrow>
                <DeleteIcon />
              </Tooltip>
            }
            onClick={handleDeleteDriver(id)}
          />,
        ],
      },
    ],
    [
      isAllowedToViewPrimaryDriversList,
      handleDriverNotes,
      deleteBookingDriverProcessing,
      // canReplaceDriver,
      handleEditAllocatedDriver,
      bookingStatusForRating,
      handleAddRating,
      canDeleteDriver,
      handleDeleteDriver,
      isCxManualAcceptEnabled,
      handleCxManualAcceptOpenDialog,
      courierExchangeLoadId,
    ],
  )

  return columns
}
export default useGetAllocatedDriversColumns
